import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Loader2, Building2, TriangleAlert } from 'lucide-react'
import AuthenticatedLayout from '../components/AuthenticatedLayout'
import { api, ApiError } from '../utils/api'
import { companiesHouseSessionService } from '../utils/companiesHouseSessionService'
import { companiesHouseFilingService } from '../utils/companiesHouseAuthService'

type CallbackState = 'idle' | 'processing' | 'success' | 'error'

const parseQueryParams = (search: string): Record<string, string> => {
  const params = new URLSearchParams(search)
  const entries: Record<string, string> = {}
  params.forEach((value, key) => {
    entries[key] = value
  })
  return entries
}

export default function CompaniesHouseAuthCallback() {
  const location = useLocation()
  const navigate = useNavigate()
  const [status, setStatus] = useState<CallbackState>('idle')
  const [message, setMessage] = useState<string>('Validating Companies House authorization...')
  const [details, setDetails] = useState<string | null>(null)
  
  // Use ref to track if callback has been processed to prevent duplicate calls
  const processedRef = useRef<string | null>(null)
  const isProcessingRef = useRef(false)

  const queryParams = useMemo(
    () => parseQueryParams(location.search),
    [location.search]
  )

  useEffect(() => {
    const exchangeCode = async () => {
      // Prevent duplicate processing
      if (status !== 'idle' || isProcessingRef.current) return

      const { code, state, error, error_description } = queryParams

      // Create a unique key for this callback to prevent duplicate processing
      const callbackKey = code ? `ch_callback_${code}` : null

      // Check if we've already processed this callback
      if (callbackKey && processedRef.current === callbackKey) {
        console.log('Callback already processed, skipping...')
        return
      }

      // Check if already processed in sessionStorage (survives page reloads)
      if (callbackKey && sessionStorage.getItem(callbackKey)) {
        console.log('Callback already processed (from sessionStorage), redirecting...')
        const redirectPath = sessionStorage.getItem('companiesHouseAuthRedirect') || '/companies-house-filing'
        sessionStorage.removeItem('companiesHouseAuthRedirect')
        navigate(redirectPath, { replace: true })
        return
      }

      if (error || error_description) {
        setStatus('error')
        setMessage('Companies House authorization was cancelled')
        setDetails(
          error_description ||
            error ||
            'You can close this window and try authorizing again.'
        )
        return
      }

      if (!code || !state) {
        setStatus('error')
        setMessage('Missing Companies House authorization parameters')
        setDetails('The Companies House callback did not include the required code/state values.')
        return
      }

      // Mark as processing
      isProcessingRef.current = true
      setStatus('processing')
      setMessage('Completing Companies House authorization...')
      setDetails(null)

      try {
        // Call the backend callback endpoint
        const response = await api.get(`/companies-house/filing/auth/callback?code=${code}&state=${state}`)
        
        // Extract response data
        const responseData = response.response as any
        const success =
          typeof responseData?.success === 'boolean'
            ? responseData.success
            : response.statusCode === 200

        if (!success) {
          setStatus('error')
          setMessage('Companies House authorization failed')
          setDetails(responseData?.message || response.message || 'Authorization failed')
          isProcessingRef.current = false
          return
        }

        // Mark this callback as processed
        if (callbackKey) {
          processedRef.current = callbackKey
          sessionStorage.setItem(callbackKey, 'processed')
        }

        // Get company number from sessionStorage (stored before auth redirect)
        const companyNumber = sessionStorage.getItem('companiesHouseCompanyNumber')
        
        if (!companyNumber) {
          setStatus('error')
          setMessage('Company number not found')
          setDetails('Unable to retrieve company number. Please try authorizing again.')
          isProcessingRef.current = false
          return
        }

        // Mark user as authorized and store company number
        const expiresAt = responseData?.data?.expiresAt || responseData?.expiresAt
        companiesHouseSessionService.setAuthorized(companyNumber, expiresAt)

        // Create a filing transaction for future use
        try {
          setMessage('Creating filing transaction...')
          const transactionResponse = await companiesHouseFilingService.createTransaction(
            companyNumber,
            'Companies House filing session',
            `SESSION-${Date.now()}`
          )

          // Extract transaction ID from response.data.id (response format: { response: { success: true, data: { id: "...", ... } } })
          const transactionData = transactionResponse.response as any
          const transactionId = transactionData?.data?.id || transactionData?.id
          
          if (transactionId) {
            // Store transaction ID for reuse
            companiesHouseSessionService.setTransactionId(transactionId)
            console.log('Transaction created:', transactionId)
          } else {
            console.warn('Transaction creation succeeded but no transaction ID returned', transactionResponse)
          }
        } catch (transactionError) {
          // Log error but don't fail the auth flow
          // Transaction can be created later when needed
          console.error('Failed to create transaction (non-critical):', transactionError)
        }

        setStatus('success')
        setMessage('Companies House authorization completed successfully.')
        
        // Check if there's a stored redirect path from filing selection
        const redirectPath = sessionStorage.getItem('companiesHouseAuthRedirect') || '/companies-house-filing'
        sessionStorage.removeItem('companiesHouseAuthRedirect')
        sessionStorage.removeItem('companiesHouseCompanyNumber')
        
        setTimeout(() => {
          navigate(redirectPath, { replace: true })
        }, 2000)
      } catch (err) {
        console.error('Companies House callback exchange failed:', err)
        isProcessingRef.current = false
        if (err instanceof ApiError) {
          setStatus('error')
          setMessage('Companies House authorization failed')
          setDetails(err.message || 'Unable to verify authorization with Companies House.')
        } else if (err instanceof Error) {
          setStatus('error')
          setMessage('Companies House authorization failed')
          setDetails(err.message)
        } else {
          setStatus('error')
          setMessage('Companies House authorization failed')
          setDetails('An unexpected error occurred. Please try again.')
        }
      }
    }

    void exchangeCode()
  }, [navigate, queryParams, status])

  return (
    <AuthenticatedLayout activeTab='filings'>
      <div className='flex min-h-[400px] items-center justify-center rounded-[15px] bg-white'>
        <div className='max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm'>
          <div className='mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100'>
            {status === 'processing' && (
              <Loader2 className='h-8 w-8 animate-spin text-gray-500' />
            )}
            {status === 'success' && (
              <Building2 className='h-8 w-8 text-green-600' />
            )}
            {status === 'error' && (
              <TriangleAlert className='h-8 w-8 text-red-600' />
            )}
          </div>
          <h1 className='mb-2 text-xl font-semibold text-gray-900'>{message}</h1>
          {details && <p className='text-sm text-gray-600'>{details}</p>}
          {status === 'success' && (
            <p className='mt-4 text-sm text-gray-500'>
              Redirecting you back to filing...
            </p>
          )}
          {status === 'error' && (
            <div className='mt-6 flex flex-col gap-3'>
              <button
                onClick={() => navigate('/companies-house-filing')}
                className='rounded-full bg-gray-900 px-5 py-2 text-sm font-semibold text-white hover:bg-gray-800'
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className='rounded-full border border-gray-300 px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100'
              >
                Return to Dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    </AuthenticatedLayout>
  )
}
